import moment, { Moment } from "moment";
import { TaskModel, TaskStatus } from "../../Models/Models";
import styles from "../../Styles/calendarDashboard.module.css";
import { Vocabulary } from "../../Utils/Vocabulary";

const HOUR_START = 8;
const HOUR_END = 19; // 08:00 – 18:00 slots (last label 18:00)
const CELL_HEIGHT = 60; // px per hour
const HOURS = Array.from({ length: HOUR_END - HOUR_START }, (_, i) => HOUR_START + i);

const STATUS_CLASS: Record<TaskStatus, string> = {
  not_started: styles.statusNotStarted,
  in_progress: styles.statusInProgress,
  completed: styles.statusCompleted,
};

type Props = {
  currentDate: Moment;
  tasks: TaskModel[];
  onTaskClick: (task: TaskModel) => void;
  onDayClick: (dateStr: string) => void;
};

type TaskDateInfo = { dayKey: string; topOffset: number; timeLabel: string };

// Prefer due_date if it has a real time; fall back to start_date; then default to top of grid
function getTaskDateInfo(task: TaskModel): TaskDateInfo | null {
  const candidates = [task.started_at, task.created_at].filter(Boolean) as string[];
  for (const dateStr of candidates) {
    const m = moment(dateStr);
    const h = m.hour();
    const min = m.minute();
    if ((h !== 0 || min !== 0) && h >= HOUR_START && h < HOUR_END) {
      return {
        dayKey: m.format("YYYY-MM-DD"),
        topOffset: (h - HOUR_START) * CELL_HEIGHT + (min / 60) * CELL_HEIGHT,
        timeLabel: m.format("HH:mm"),
      };
    }
  }
  // No specific time — pin to first visible hour on the started/created day
  const fallback = task.started_at ?? task.created_at;
  if (!fallback) return null;
  return {
    dayKey: moment(fallback).format("YYYY-MM-DD"),
    topOffset: 0,
    timeLabel: "",
  };
}

function groupByDay(tasks: TaskModel[]): Record<string, { info: TaskDateInfo; task: TaskModel }[]> {
  const map: Record<string, { info: TaskDateInfo; task: TaskModel }[]> = {};
  tasks.forEach((task) => {
    const info = getTaskDateInfo(task);
    if (!info) return;
    if (!map[info.dayKey]) map[info.dayKey] = [];
    map[info.dayKey].push({ info, task });
  });
  return map;
}

function buildWeekDays(date: Moment): Moment[] {
  const monday = date.clone().startOf("isoWeek");
  return Array.from({ length: 7 }, (_, i) => monday.clone().add(i, "days"));
}

export default function CalendarWeekView({ currentDate, tasks, onTaskClick, onDayClick }: Props) {
  const today = moment().format("YYYY-MM-DD");
  const weekDays = buildWeekDays(currentDate);
  const tasksByDay = groupByDay(tasks);

  const dayNames = [
    Vocabulary.calendarDayMon,
    Vocabulary.calendarDayTue,
    Vocabulary.calendarDayWed,
    Vocabulary.calendarDayThu,
    Vocabulary.calendarDayFri,
    Vocabulary.calendarDaySat,
    Vocabulary.calendarDaySun,
  ];

  return (
    <div className={styles.calendarWrapper}>
      <div className={styles.weekWrapper}>
        {/* Single scroll container — header is sticky inside it so columns always align */}
        <div className={styles.weekScrollBody}>

          {/* Sticky header row */}
          <div className={styles.weekHeaderRow}>
            <div className={styles.weekHeaderGutter} />
            {weekDays.map((day, index) => {
              const dayKey = day.format("YYYY-MM-DD");
              const isToday = dayKey === today;
              return (
                <div key={dayKey} className={styles.weekDayHeader}>
                  <span className={styles.weekDayName}>{dayNames[index]}</span>
                  <span className={`${styles.weekDayNumber} ${isToday ? styles.weekDayNumberToday : ""}`}>
                    {day.date()}
                  </span>
                </div>
              );
            })}
          </div>

          {/* Time grid */}
          <div className={styles.weekTimeBody}>
            {/* Hour labels */}
            <div className={styles.weekTimeLabelsCol}>
              {HOURS.map((hour) => (
                <div key={hour} className={styles.weekTimeLabelCell}>
                  {`${String(hour).padStart(2, "0")}:00`}
                </div>
              ))}
            </div>

            {/* Day columns */}
            {weekDays.map((day) => {
              const dayKey = day.format("YYYY-MM-DD");
              const dayTasks = tasksByDay[dayKey] ?? [];
              return (
                <div
                  key={dayKey}
                  className={styles.weekDayTimeCol}
                  onClick={() => onDayClick(dayKey)}
                >
                  {/* Hourly grid lines */}
                  {HOURS.map((hour) => (
                    <div key={hour} className={styles.weekHourLine} />
                  ))}

                  {/* Task blocks */}
                  {dayTasks.map(({ info, task }) => {
                    const statusClass = STATUS_CLASS[task.status] ?? styles.statusNotStarted;
                    return (
                      <div
                        key={task.id}
                        className={`${styles.weekTimedChip} ${statusClass}`}
                        style={{ top: info.topOffset, height: CELL_HEIGHT - 6 }}
                        onClick={(e) => { e.stopPropagation(); onTaskClick(task); }}
                        title={info.timeLabel ? `${task.name} — ${info.timeLabel}` : task.name}
                      >
                        {info.timeLabel && (
                          <span className={styles.weekTimedChipTime}>{info.timeLabel}</span>
                        )}
                        <span className={styles.weekTimedChipName}>{task.name}</span>
                      </div>
                    );
                  })}
                </div>
              );
            })}
          </div>

        </div>
      </div>
    </div>
  );
}
